import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import Button from "../common/Button";
import Input from "../common/Input";
import LocationMapPicker from "./LocationMapPicker";
import EventAreaGuideModal, { EventAreaGuidePreviewCard } from "./EventAreaGuideModal";
import {
  generateELQR,
  updateELQR,
  createLocationCheckInPoint,
  updateLocationCheckInPoint,
  deleteLocationCheckInPoint,
} from "../../services/createEventService";
import { buildLocationPayload, getCreatedLocationId, savedLocationToZonePoints } from "../../utils/eventLocation";
import { doesPolygonSelfIntersect } from "../../utils/eventZone";

/**
 * Create/edit a saved location (ELQR): address + radius, optional geofence
 * zone, optional check-in points. Ported from the mobile app's
 * src/Screens/add-location (controller + form + event-zone-editor + check-in
 * points section), condensed into a single modal with step-swapping instead
 * of separate screens.
 */
const AddLocationModal = ({ locationObj = null, defaultName, onClose, onSaved }) => {
  const { t } = useTranslation(["events", "common"]);
  const isEdit = Boolean(locationObj?.Id);
  const [step, setStep] = useState("main"); // 'main' | 'zone' | 'checkIn'
  const [showAreaGuide, setShowAreaGuide] = useState(false);
  const [name, setName] = useState(locationObj?.LocationName || defaultName || t("addLocationModal.newLocationDefault"));
  const [pick, setPick] = useState(
    locationObj
      ? {
          latitude: locationObj.Latitude,
          longitude: locationObj.Longitude,
          address: locationObj.Address,
          city: locationObj.City,
          country: locationObj.Country,
          radius: locationObj.Radius,
        }
      : null
  );
  const [zonePoints, setZonePoints] = useState(() =>
    locationObj ? savedLocationToZonePoints(locationObj) : []
  );
  const [checkInPoints, setCheckInPoints] = useState(
    () => locationObj?.LocationCheckInPoints?.map((p) => ({ ...p })) ?? []
  );
  const [originalCheckInIds] = useState(
    () => new Set((locationObj?.LocationCheckInPoints ?? []).map((p) => p.id).filter((id) => id != null))
  );
  const [saving, setSaving] = useState(false);

  const syncCheckInPoints = async (promoterELQRId) => {
    const currentServerIds = new Set(
      checkInPoints.map((p) => p.id).filter((id) => originalCheckInIds.has(id))
    );
    const deletedIds = [...originalCheckInIds].filter((id) => !currentServerIds.has(id));
    const validPoints = checkInPoints.filter((p) => p.latitude && p.longitude);

    const tasks = [
      ...validPoints.map((point, index) => {
        const pointName = point.name?.trim() || t("addLocationModal.gateDefaultName", { index: index + 1 });
        if (originalCheckInIds.has(point.id)) {
          return updateLocationCheckInPoint({
            id: point.id,
            name: pointName,
            latitude: Number(point.latitude),
            longitude: Number(point.longitude),
            radius: Number(point.radius),
            isActive: true,
          });
        }
        return createLocationCheckInPoint({
          promoterELQRId,
          name: pointName,
          latitude: Number(point.latitude),
          longitude: Number(point.longitude),
          radius: Number(point.radius),
        });
      }),
      ...deletedIds.map((id) => deleteLocationCheckInPoint(id)),
    ];

    if (!tasks.length) return;
    const results = await Promise.allSettled(tasks);
    if (results.some((r) => r.status === "rejected")) {
      toast.warning(t("addLocationModal.toasts.checkInSyncFailed"));
    }
  };

  const handleSave = async () => {
    if (!pick?.latitude || !pick?.longitude) {
      toast.warning(t("addLocationModal.toasts.chooseLocationFirst"));
      return;
    }
    if (zonePoints.length > 0 && zonePoints.length < 3) {
      toast.warning(t("addLocationModal.toasts.zoneMinPoints"));
      return;
    }
    if (doesPolygonSelfIntersect(zonePoints)) {
      toast.warning(t("addLocationModal.toasts.zoneSelfIntersects"));
      return;
    }

    const body = buildLocationPayload({ name: name.trim() || t("addLocationModal.newLocationDefault"), pick, zoneCoordinates: zonePoints });

    setSaving(true);
    try {
      if (isEdit) {
        await updateELQR(locationObj.Id, body);
        await syncCheckInPoints(locationObj.Id);
        toast.success(t("addLocationModal.toasts.updated"));
      } else {
        const res = await generateELQR(body);
        const newId = getCreatedLocationId(res.data);
        if (newId) await syncCheckInPoints(newId);
        toast.success(t("addLocationModal.toasts.saved"));
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("addLocationModal.toasts.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (step === "zone") {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={{ ...modalStyle, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h3 style={styles.title}>{t("addLocationModal.zoneTitle")}</h3>
            <button style={styles.closeBtn} onClick={() => setStep("main")}><X size={16} strokeWidth={2} /></button>
          </div>
          <LocationMapPicker
            mode="zone"
            value={zonePoints}
            onChange={setZonePoints}
            center={pick ? { latitude: pick.latitude, longitude: pick.longitude } : undefined}
            height={360}
          />
          <div style={styles.actions}>
            {zonePoints.length > 0 && (
              <Button variant="danger" type="button" onClick={() => setZonePoints([])}>
                {t("addLocationModal.removeZone")}
              </Button>
            )}
            <Button
              type="button"
              disabled={zonePoints.length > 0 && (zonePoints.length < 3 || doesPolygonSelfIntersect(zonePoints))}
              onClick={() => setStep("main")}
            >
              {t("common:actions.done")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "checkIn") {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={{ ...modalStyle, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h3 style={styles.title}>{t("addLocationModal.checkInTitle")}</h3>
            <button style={styles.closeBtn} onClick={() => setStep("main")}><X size={16} strokeWidth={2} /></button>
          </div>
          <LocationMapPicker
            mode="checkIn"
            value={checkInPoints}
            onChange={setCheckInPoints}
            center={pick ? { latitude: pick.latitude, longitude: pick.longitude } : undefined}
            height={360}
          />
          <div style={styles.actions}>
            <Button type="button" onClick={() => setStep("main")}>{t("common:actions.done")}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>{isEdit ? t("addLocationModal.editTitle") : t("addLocationModal.addTitle")}</h3>
          <button style={styles.closeBtn} onClick={onClose}><X size={16} strokeWidth={2} /></button>
        </div>

        <Input label={t("addLocationModal.locationNameLabel")} value={name} onChange={(e) => setName(e.target.value)} maxLength={24} />

        <div style={{ marginBottom: 16 }}>
          <label style={styles.fieldLabel}>{t("addLocationModal.addressLabel")}</label>
          <LocationMapPicker mode="pick" value={pick} onChange={setPick} height={300} />
        </div>

        <EventAreaGuidePreviewCard onPress={() => setShowAreaGuide(true)} />

        <div style={{ ...styles.sectionRow, marginTop: 4 }}>
          <div>
            <div style={styles.sectionTitle}>{t("addLocationModal.zoneSectionTitle")}</div>
            <div style={styles.sectionSub}>
              {zonePoints.length >= 3 ? t("addLocationModal.zonePointsSet", { count: zonePoints.length }) : t("addLocationModal.noZoneSet")}
            </div>
          </div>
          <Button variant="secondary" type="button" onClick={() => setStep("zone")}>
            {zonePoints.length >= 3 ? t("addLocationModal.editZone") : t("addLocationModal.addZone")}
          </Button>
        </div>

        <div style={styles.sectionRow}>
          <div>
            <div style={styles.sectionTitle}>{t("addLocationModal.checkInSectionTitle")}</div>
            <div style={styles.sectionSub}>
              {checkInPoints.length > 0 ? t("addLocationModal.checkInPinsSet", { count: checkInPoints.length }) : t("addLocationModal.noCheckInPins")}
            </div>
          </div>
          <Button variant="secondary" type="button" onClick={() => setStep("checkIn")}>
            {t("addLocationModal.managePins")}
          </Button>
        </div>

        <div style={styles.actions}>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? t("addLocationModal.savingButton") : t("addLocationModal.saveLocation")}
          </Button>
        </div>
      </div>

      {showAreaGuide && <EventAreaGuideModal onClose={() => setShowAreaGuide(false)} />}
    </div>
  );
};

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1200,
  padding: 16,
  overflowY: "auto",
};

const modalStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
  width: "100%",
  maxWidth: 460,
  boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
  maxHeight: "90vh",
  overflowY: "auto",
};

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  closeBtn: { display: "flex", background: "none", border: "none", cursor: "pointer", color: "#888" },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14, color: "#444" },
  sectionRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 0", borderTop: "1px solid #f0f0f0",
  },
  sectionTitle: { fontSize: 13, fontWeight: 600, color: "#1e1e2d" },
  sectionSub: { fontSize: 11, color: "#888", marginTop: 2 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 },
};

export default AddLocationModal;
